"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowRight, PiArrowRightFill } from "react-icons/pi";

export default function OvanAbout({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const aboutImage = content.aboutImage || "/assets/3-DVVA5i4W.jpg";
  const ceoAvatar = content.ceoAvatar || "/assets/1-BcvSHoAR.jpg";
  
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
      };
    }
  }, []);

  const defaultBulletPoints = [
    "Digital Marketing Solutions for Tomorrow",
    "Our Talented & Experienced Marketing Agency",
    "Create your own skin to match your brand",
  ];

  const bulletPoints = content.bulletPoints || defaultBulletPoints;
  const workingSpaces = content.workingSpaces || ["Full Time", "Part Time", "Remote"];

  const updateBullet = (idx: number, val: string) => {
    if (!onContentChange) return;
    const newBullets = [...bulletPoints];
    newBullets[idx] = val;
    onContentChange({ ...content, bulletPoints: newBullets });
  };

  return (
    <section
      id="about"
      className={`relative py-16 md:py-24 transition-colors duration-200 ${
        isDark ? "bg-slate-900" : "bg-white"
      } ${
        isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
      }`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-12">
          {/* Left image column with badges */}
          <div className="md:col-span-5 relative">
            <div className="relative max-w-sm mx-auto md:mx-0">
              <img
                className="mx-auto rounded-3xl shadow-lg w-[90%] object-cover"
                alt="About us"
                src={aboutImage}
              />
              
              {/* Working spaces floating card */}
              {!content.hideWorkingSpaces && (
                <div className={`absolute bottom-16 -left-5 p-4 rounded-xl shadow-lg border transition-all duration-200 ${
                  isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-100"
                }`}>
                  <span className={`text-sm font-semibold block mb-2 ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}>
                    Working Spaces
                  </span>
                  <div className="flex gap-1.5 flex-wrap">
                    {workingSpaces.map((ws: string, tagIdx: number) => (
                      <span key={tagIdx} className="py-0.5 px-2 rounded-xl bg-blue-600/5 text-blue-600 font-semibold text-xs">
                        {ws}
                      </span>
                    ))}
                  </div>
                </div>
              )}
 
              {/* CEO profile card */}
              {!content.hideCEO && (
                <div className={`absolute flex items-center top-16 -right-5 p-4 rounded-xl shadow-lg border transition-all duration-200 ${
                  isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-100"
                }`}>
                  <img
                    className="size-12 rounded-full shadow-sm object-cover"
                    alt="Calvin Carlo"
                    src={ceoAvatar}
                  />
                  <div className="ms-3">
                    <span className={`text-sm font-semibold block ${
                      isDark ? "text-white" : "text-slate-900"
                    }`}>
                      <EditableText
                        value={content.ceoName || "Mr. Calvin Carlo"}
                        onChange={(val) =>
                          onContentChange && onContentChange({ ...content, ceoName: val })
                        }
                        as="span"
                        placeholder="CEO Name"
                      />
                    </span>
                    <span className="text-slate-400 text-xs block">
                      <EditableText
                        value={content.ceoTitle || "CEO / Owner"}
                        onChange={(val) =>
                          onContentChange && onContentChange({ ...content, ceoTitle: val })
                        }
                        as="span"
                        placeholder="CEO Title"
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right text details column */}
          <div className="md:col-span-7">
            <div className="lg:ms-8">
              <h3 className={`mb-4 md:leading-normal text-3xl font-semibold transition-colors ${
                isDark ? "text-white" : "text-slate-900"
              }`}>
                <EditableText
                  value={content.title || "Simplicity is one of the best qualities."}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, title: val })
                  }
                  as="span"
                  className={isDark ? "text-white" : "text-slate-900"}
                  placeholder="About Title"
                />
              </h3>
              <p className="text-slate-400 leading-relaxed mb-6">
                <EditableText
                  value={
                    content.text ||
                    "Due to its widespread use as filler text for layouts, non-readability is of great importance: human perception is tuned to recognize certain patterns and repetitions in texts."
                  }
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, text: val })
                  }
                  as="span"
                  placeholder="About Body Copy"
                />
              </p>

              <ul className={`list-none space-y-3 my-6 transition-colors ${
                isDark ? "text-slate-300" : "text-slate-500"
              }`}>
                {bulletPoints.map((bullet: string, idx: number) => (
                  <li key={idx} className="flex items-center">
                    <PiArrowRight className="text-blue-600 me-3 size-4 flex-shrink-0" />
                    <span>
                      <EditableText
                        value={bullet}
                        onChange={(val) => updateBullet(idx, val)}
                        as="span"
                        placeholder="Bullet Point"
                      />
                    </span>
                  </li>
                ))}
              </ul>

              {!content.hideReadMore && (
                <a
                  href={content.readMoreLink || "/aboutus"}
                  className="h-10 px-5 tracking-wider inline-flex items-center text-xs uppercase justify-center font-bold rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-md"
                >
                  <EditableText
                    value={content.btnText || "Read More"}
                    onChange={(val) =>
                      onContentChange && onContentChange({ ...content, btnText: val })
                    }
                    as="span"
                    placeholder="About CTA Text"
                  />
                  <PiArrowRightFill className="ms-1 size-3.5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
